import * as React from 'react';
import type { Editor } from '@tiptap/react';

import type { CommentAuthor } from '../../../../ui/tiptap/extensions/comment-mark';

/**
 * Which kind of editor a provider takes its threads from.
 *
 * A standalone document page comments on *its* document, even while a resource
 * mention has opened some other document in the panel beside it. The chat page has no
 * document of its own and comments on whichever artifact is in front. Both kinds of
 * editor can be mounted under the same provider at once, so each one says what it is
 * and the provider keeps only the kind it was opened for — otherwise which editor the
 * rail points at would come down to which effect React happened to run last.
 */
export type CommentTargetSource = 'page' | 'artifact';

export interface CommentTarget {
  /** Identifies the publisher, so only it can retract its own target. */
  key: string;
  editor: Editor | null;
  /** Stamped onto anything written in the index, and decides whose messages it offers to edit. */
  author: CommentAuthor | null;
}

interface CommentsRailContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  target: CommentTarget | null;
  /**
   * The thread the index has opened, if any.
   *
   * Published rather than kept inside the index because the floating card over the
   * prose reads it: both open off the same caret, and with the thread already on
   * screen in a column a second copy of it over the text is one too many.
   */
  openThreadId: string | null;
  setOpenThreadId: (commentId: string | null) => void;
  accepts: CommentTargetSource;
  publish: (
    source: CommentTargetSource,
    key: string,
    target: CommentTarget | null
  ) => void;
}

const CommentsRailContext =
  React.createContext<CommentsRailContextValue | null>(null);

export function useCommentsRail(): CommentsRailContextValue | null {
  return React.useContext(CommentsRailContext);
}

/**
 * Holds the comments rail's open state and the editor it is pointed at.
 *
 * Wraps a whole page, above the header, because the toggle in the header and the
 * column down in the layout are the two ends of one piece of state and have no nearer
 * ancestor in common.
 */
export const CommentsRailProvider: React.FC<{
  /** See {@link CommentTargetSource}. */
  accepts: CommentTargetSource;
  children: React.ReactNode;
}> = ({ accepts, children }) => {
  const [open, setOpenState] = React.useState(false);
  const [openThreadId, setOpenThreadId] = React.useState<string | null>(null);

  // Closing puts the column back to the list. A thread left open behind a closed
  // panel is state nobody can see, and the surfaces that ask "is this thread already
  // showing?" would go on being told yes about a column that is not there.
  const setOpen = React.useCallback((next: boolean) => {
    setOpenState(next);
    if (!next) {
      setOpenThreadId(null);
    }
  }, []);

  const toggle = React.useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);
  const [target, setTarget] = React.useState<CommentTarget | null>(null);

  const publish = React.useCallback(
    (source: CommentTargetSource, key: string, next: CommentTarget | null) => {
      if (source !== accepts) {
        return;
      }
      setTarget((previous) => {
        if (next !== null) {
          return next;
        }
        // Only the publisher that owns the current target may clear it. An editor
        // unmounting after another has already taken over would otherwise blank the
        // rail that is now somebody else's.
        return previous?.key === key ? null : previous;
      });
    },
    [accepts]
  );

  const value = React.useMemo<CommentsRailContextValue>(
    () => ({
      open,
      setOpen,
      toggle,
      target,
      openThreadId,
      setOpenThreadId,
      accepts,
      publish,
    }),
    [accepts, open, openThreadId, publish, setOpen, target, toggle]
  );

  return (
    <CommentsRailContext.Provider value={value}>
      {children}
    </CommentsRailContext.Provider>
  );
};

export interface PublishCommentTargetArgs {
  source: CommentTargetSource;
  /** Stable per editor — the resource's id. */
  key: string;
  /**
   * Whether this editor is the one the rail should be showing. False for a document
   * sitting on an artifact tab that is not in front.
   */
  active: boolean;
  editor: Editor | null;
  authorId: string;
  authorName: string;
}

/**
 * Offers this editor to the comments rail above it, for as long as it is the active
 * one. Does nothing when no rail is mounted, which is the case for an editor rendered
 * somewhere that has no room for a column.
 *
 */
export function usePublishCommentTarget({
  source,
  key,
  active,
  editor,
  authorId,
  authorName,
}: PublishCommentTargetArgs): void {
  const rail = useCommentsRail();
  const publish = rail?.publish;

  const target = React.useMemo<CommentTarget | null>(
    () =>
      active
        ? { key, editor, author: { id: authorId, name: authorName } }
        : null,
    [active, authorId, authorName, editor, key]
  );

  React.useEffect(() => {
    if (publish === undefined) {
      return;
    }
    publish(source, key, target);
    return () => publish(source, key, null);
  }, [key, publish, source, target]);
}
